/**
 * 이용자 관리 (슈퍼관리자용)
 * user-management-admin의 내부 컴포넌트들을 재사용하여 확장성과 유지보수성을 확보합니다.
 * 메인 컴포넌트는 역할별로 분리되어 있어 향후 슈퍼관리자 전용 기능 추가 시 독립적으로 확장 가능합니다.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { adminAPI } from '@/services/api';
import { User } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification, useDebounce } from '@/hooks/common';
import {
  useUserManagementActions,
  useUserManagementInvite,
  useUserManagementState,
  useUserManagementBlockedLogs,
  useUserManagementTabs,
} from '@/hooks/userManagement';
import { UserManagementProvider, useUserManagementContext } from '@/contexts/userManagement';
// user-management-admin의 내부 컴포넌트 재사용
import { UserManagementHeader } from '@/components/users/management/common/UserManagementHeader';
import {
  PendingUsersTab,
  CustomerUsersTab,
  AllUsersTab,
  BlockedListTab,
  UserSettingsTab,
} from '@/components/users/management/superadmin/tabs';
import {
  EditUserDialog,
  ChangeUserPasswordDialog,
  AuthTypeChangeDialog,
  ConfirmDialog,
  InviteUserDialogWithCustomer,
  AlertDialog,
} from '@/components/users/management/common/dialogs';
import { getRoleConfig, UserRole } from '@/utils/roleConfig';
import { USER_MANAGEMENT_TABS, type UserManagementTab } from '@/constants/users';
import { SUBMENU_VALUES } from '@/constants/routing';
import { NOTIFICATION } from '@/constants';

/**
 * 슈퍼관리자 전용 화면 (Super Admin View)
 * 
 * 페이지 위치: frontend/src/components/users/management/superadmin/index.tsx
 * 
 * 역할:
 * - 이 화면은 슈퍼관리자(saas_admin 역할)만 접근할 수 있는 전용 화면입니다.
 * - 모든 고객사의 정보를 조회하고 관리할 수 있습니다.
 * - 고객사 필터, 조직 정보 등 모든 슈퍼관리자 전용 기능이 활성화됩니다.
 * 
 * 보안 원칙:
 * - 이 화면의 컴포넌트는 isSaasAdmin={isSaasAdmin}로 전달합니다 (실제 역할 체크 결과 사용).
 * - 모든 고객사 정보를 표시하는 필터와 컬럼이 표시됩니다.
 * - customers prop에 모든 고객사 목록을 전달합니다.
 * 
 * 기업 관리자 화면이 필요한 경우:
 * - admin/index.tsx (기업 관리자 화면)를 사용하세요.
 *   (슈퍼관리자도 기업 관리자 화면에 접근 가능하지만, 접근 시 기업 관리자 모드로 동작)
 * 
 * 용어 참고: docs/2.architecture/7.TERMINOLOGY.md
 */
const UserManagementInner: React.FC = () => {
  const { user: currentUser } = useAuth();
  const userRole = (currentUser?.role || 'user') as UserRole;
  const roleConfig = getRoleConfig(userRole);
  // 슈퍼관리자 전용 화면이므로 실제 슈퍼관리자 권한을 사용합니다.
  const isSaasAdmin = userRole === 'saas_admin'; // 역할 체크
  const isAdmin = userRole === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const subtabFromUrl = searchParams.get('subtab') as UserManagementTab | null;
  const customerIdFromUrl = searchParams.get('customerId');
  const submenu = searchParams.get('submenu');
  
  // "내 이용자 관리"인지 "전체 이용자 관리"인지 확인
  const isMyUserManagement = submenu === SUBMENU_VALUES.USER_MANAGEMENT;
  const isAllUserManagement = submenu === SUBMENU_VALUES.USER_MANAGEMENT_ALL;
  
  // 고객사 ID 설정:
  // - 기업관리자 또는 "내 이용자 관리": 자신의 고객사 ID 사용
  // - "전체 이용자 관리": null (전체 조회)
  const getInitialCustomerId = (): number | null => {
    if (customerIdFromUrl) {
      const parsed = parseInt(customerIdFromUrl, 10);
      return Number.isNaN(parsed) ? null : parsed;
    }
    if (isAllUserManagement) {
      // "전체 이용자 관리": URL에 값 없으면 전체(null)
      return null;
    }
    // 기업관리자 또는 "내 이용자 관리": 자신의 고객사 ID 사용
    return currentUser?.customer?.id || null;
  };
  
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    getInitialCustomerId()
  );
  
  // 기업관리자와 "내 이용자 관리"는 기본 탭을 'all'로 설정 (전체사용자)
  // 슈퍼관리자도 "내 이용자 관리"로 들어오면 기본 탭은 'all'
  const defaultTab: UserManagementTab = (isAdmin || isMyUserManagement) ? USER_MANAGEMENT_TABS.ALL : USER_MANAGEMENT_TABS.PENDING;
  
  // URL에서 직접 읽어서 항상 최신 값 사용 (상태 동기화 문제 방지)
  const activeTab: UserManagementTab = subtabFromUrl || defaultTab;
  
  // URL 파라미터 초기화 (한 번만)
  const hasInitializedTab = useRef(false);
  useEffect(() => {
    if (!hasInitializedTab.current && !subtabFromUrl) {
      hasInitializedTab.current = true;
      const params: Record<string, string> = { subtab: defaultTab };
      if (selectedCustomerId) {
        params.customerId = selectedCustomerId.toString();
      }
      setSearchParams(params, { replace: true });
    }
  }, [subtabFromUrl, defaultTab, setSearchParams, selectedCustomerId]);
  
  // customerId가 변경되지 않도록 고정
  useEffect(() => {
    if (isAllUserManagement) {
      if (customerIdFromUrl) {
        const parsed = parseInt(customerIdFromUrl, 10);
        if (!Number.isNaN(parsed) && selectedCustomerId !== parsed) {
          setSelectedCustomerId(parsed);
        }
      } else if (selectedCustomerId !== null) {
        setSelectedCustomerId(null);
      }
    } else if (isAdmin || isMyUserManagement) {
      const expectedCustomerId = currentUser?.customer?.id || null;
      if (selectedCustomerId !== expectedCustomerId) {
        setSelectedCustomerId(expectedCustomerId);
      }
    }
  }, [
    isAdmin,
    isMyUserManagement,
    isAllUserManagement,
    currentUser?.customer?.id,
    selectedCustomerId,
    customerIdFromUrl,
  ]);

  const handleSubTabChange = (tab: string) => {
    const validTab = tab as UserManagementTab;
    // 즉시 URL 업데이트 (상태는 URL에서 자동으로 읽어옴)
    const params: Record<string, string> = { subtab: validTab };
    // customerId가 있으면 유지
    if (selectedCustomerId) {
      params.customerId = selectedCustomerId.toString();
    }
    setSearchParams(params, { replace: true });
  };

  const { error, success, setError, setSuccess } = useNotification({ duration: NOTIFICATION.LONG_DURATION });
  const [loading, setLoading] = useState(false);
  
  // Context에서 공통 상태 가져오기
  const {
    editingUser,
    setEditingUser,
    originalUser,
    setOriginalUser,
    passwordChangeUser,
    setPasswordChangeUser,
    authTypeChangeUser,
    setAuthTypeChangeUser,
    alertOpen,
    setAlertOpen,
    alertMessage,
    setAlertMessage,
    alertTitle,
    setAlertTitle,
    showAlert,
  } = useUserManagementContext();
  
  // 나머지 상태 관리 hook (그룹화된 구조)
  const { search, setSearch, blocked, setBlocked, counts, setCounts, refresh, customers } = useUserManagementState();

  // 디바운스된 검색어 (AllUsersTab용)
  const debouncedAllUsersSearchQuery = useDebounce(search.allUsers, 500);
  
  // 차단목록 로직 hook
  // getCustomerId를 useCallback으로 메모이제이션하여 무한 루프 방지
  const getCustomerId = React.useCallback(() => {
    return isAllUserManagement ? null : (selectedCustomerId || currentUser?.customer?.id || null);
  }, [isAllUserManagement, selectedCustomerId, currentUser?.customer?.id]);
  
  // 차단목록 로직 hook (그룹화된 구조 사용)
  const { loadBlockedLogs, handleClearBlockedLogsClick, handleClearBlockedLogs } = useUserManagementBlockedLogs({
    blocked,
    setBlocked,
    showAlert,
    getCustomerId,
    isAllUserManagement,
  });

  // Hooks 사용 (그룹화된 구조)
  const {
    confirmDialogs,
    setConfirmDialogs,
    approvingUserId,
    handlers,
  } = useUserManagementActions({
    setError,
    setSuccess,
    setLoading,
    refreshAllTabs: () => refreshAllTabsRef.current(),
  });

  const {
    showInviteForm,
    setShowInviteForm,
    inviteLoading,
    handleInviteUsers,
  } = useUserManagementInvite({
    selectedCustomerId,
    currentUserCustomerId: currentUser?.customer?.id,
    setError,
    setSuccess,
    refreshAllTabs: () => refreshAllTabsRef.current(),
  });

  const handleCreateCustomerFromUser = async (user: User) => {
    setAlertTitle('알림');
    setAlertMessage(`사용자 ${user.email}를 위한 고객사를 생성합니다.`);
    setAlertOpen(true);
  };

  const handleUpdateUser = async (user: User, updates?: { auth_type?: string; new_password?: string }) => {
    if (!originalUser) return;
    await handlers.update(user, originalUser, updates);
    setEditingUser(null);
    setOriginalUser(null);
  };

  // 고객사 목록 불러오기 (SaaS 관리자용)
  useEffect(() => {
    if (isSaasAdmin) {
      const fetchCustomers = async () => {
        try {
          const response = await adminAPI.getCustomers();
          customers.setList(response.customers || []);
        } catch (error) {
          console.error('고객사 목록 불러오기 실패:', error);
        }
      };
      fetchCustomers();
    }
  }, [isSaasAdmin, customers.setList]);

  // 차단목록 탭이 선택되거나 includeDeleted가 변경될 때 데이터 로드
  // loadBlockedLogs는 useCallback으로 메모이제이션되어 있으므로 안전하게 의존성 배열에 포함
  useEffect(() => {
    if (activeTab === USER_MANAGEMENT_TABS.BLOCKED) {
      loadBlockedLogs();
    }
  }, [activeTab, blocked.includeDeleted, loadBlockedLogs]);

  // 탭 카운트 및 새로고침 로직 hook (그룹화된 구조 사용)
  const { refreshAllTabsRef } = useUserManagementTabs({
    setCounts,
    triggerRefresh: refresh.trigger,
    loadBlockedLogs,
    isMyUserManagement,
    isAdmin,
    isAllUserManagement,
    selectedCustomerId,
    activeTab, // superadmin은 activeTab을 전달하여 차단목록 탭이 아닐 때도 카운트 갱신
  });


  return (
    <div className="space-y-6">
      <UserManagementHeader
        loading={loading}
        onInviteClick={() => setShowInviteForm(true)}
        onRefresh={() => refreshAllTabsRef.current()}
      />

      {error && (
        <AlertMessage 
          type="error" 
          message={error} 
          onClose={() => setError('')} 
        />
      )}

      {success && (
        <AlertMessage 
          type="success" 
          message={success} 
          onClose={() => setSuccess('')} 
        />
      )}

      <Tabs value={activeTab} onValueChange={handleSubTabChange}>
        <TabsList
          className={`grid w-full ${
            (() => {
              // "내 이용자 관리": 전체사용자 / 승인대기중 / 차단목록 / 이용자설정 (4개)
              if (isMyUserManagement || isAdmin) {
                return 'grid-cols-4';
              }
              // "전체 이용자 관리": 전체사용자 / 승인대기중 / 차단목록 (3개)
              if (isAllUserManagement) {
                return 'grid-cols-3';
              }
              // 기본값 (고객사별 사용자 탭 제거됨)
              const baseCount =
                (roleConfig.userManagement.showPendingTab ? 1 : 0) +
                (roleConfig.userManagement.showAllUsersTab ? 1 : 0);
              const extraTabs = isAdmin ? 2 : 0;
              const tabCount = baseCount + extraTabs;
              if (tabCount === 1) return 'grid-cols-1';
              if (tabCount === 2) return 'grid-cols-2';
              if (tabCount === 3) return 'grid-cols-3';
              if (tabCount === 4) return 'grid-cols-4';
              return 'grid-cols-2';
            })()
          }`}
        >
          {/* 승인대기중 탭을 가장 앞에 배치 (기본 탭) */}
          {roleConfig.userManagement.showPendingTab && (
            <TabsTrigger value="pending">
              {`승인대기중 (${counts.pending})`}
            </TabsTrigger>
          )}
          {/* 전체사용자 탭 */}
          {roleConfig.userManagement.showAllUsersTab && (
            <TabsTrigger value={USER_MANAGEMENT_TABS.ALL}>
              {`${roleConfig.userManagement.allUsersTabLabel} (${counts.allUsers})`}
            </TabsTrigger>
          )}
          {/* 고객사별 사용자 탭 제거됨 */}
          {/* 차단목록: "내 이용자 관리", 기업관리자, 또는 "전체 이용자 관리"에서 표시 */}
          {(isMyUserManagement || isAdmin || isAllUserManagement) && (
            <TabsTrigger value={USER_MANAGEMENT_TABS.BLOCKED}>
              {`차단목록 (${counts.blocked})`}
            </TabsTrigger>
          )}
          {(isMyUserManagement || isAdmin) && (
            <TabsTrigger value={USER_MANAGEMENT_TABS.SETTINGS}>이용자설정</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value={USER_MANAGEMENT_TABS.PENDING} className="space-y-4">
          <PendingUsersTab
            key={refresh.key}
            initialCustomerId={isAllUserManagement ? null : selectedCustomerId}
            customers={customers.list}
            showCustomerFilter={true}
            useBackendFiltering={true}
            onEditUser={(user) => {
              setEditingUser({ ...user });
              setOriginalUser({ ...user });
            }}
            onApproveUser={handlers.approve}
            onCreateCustomer={handleCreateCustomerFromUser}
            showCreateCustomer={isSaasAdmin && isAllUserManagement}
            onRefresh={() => refreshAllTabsRef.current()}
          />
        </TabsContent>

        {/* 고객사별 사용자 탭 제거됨 */}

        {roleConfig.userManagement.showAllUsersTab && (
          <TabsContent value={USER_MANAGEMENT_TABS.ALL} className="space-y-4">
            {isAdmin || isMyUserManagement ? (
              // 기업관리자 또는 슈퍼관리자의 "내 이용자 관리": 자신의 기업 내 전체 사용자 (customerId 자동 설정)
              <CustomerUsersTab
                key={refresh.key}
                customerId={selectedCustomerId}
                searchQuery={search.allUsers}
                debouncedSearchQuery={debouncedAllUsersSearchQuery}
                onSearchChange={(query) => setSearch({ allUsers: query })}
                // 기업관리자와 슈퍼관리자의 "내 이용자 관리"는 고객사 선택/뒤로가기 기능이 필요 없으므로 no-op 핸들러 전달
                onSelectCustomer={() => {}}
                onBack={() => {}}
                hideCustomerSelection={true}
                onEditUser={(user) => {
                  setEditingUser({ ...user });
                  setOriginalUser({ ...user });
                }}
                onPasswordChange={setPasswordChangeUser}
                onAuthTypeChange={setAuthTypeChangeUser}
                onApproveUser={handlers.approve}
                onDeactivateUser={handlers.deactivate.click}
                approvingUserId={approvingUserId}
              />
            ) : (
              // 슈퍼관리자의 "전체 이용자 관리": 전체 사용자
              <AllUsersTab
                key={refresh.key}
                initialCustomerId={customerIdFromUrl ? parseInt(customerIdFromUrl, 10) : null}
                showCustomerFilter={true}
                showCustomerColumn={true}
                onEditUser={(user) => {
                  setEditingUser({ ...user });
                  setOriginalUser({ ...user });
                }}
                onDeactivateUser={handlers.deactivate.click}
                refreshKey={refresh.key}
              />
            )}
          </TabsContent>
        )}

        {(isMyUserManagement || isAdmin || isAllUserManagement) && (
          <TabsContent value={USER_MANAGEMENT_TABS.BLOCKED} className="space-y-4">
            <BlockedListTab
              logs={blocked.logs}
              loading={blocked.loading}
              onRefresh={() => refreshAllTabsRef.current()}
              onClear={isAllUserManagement ? undefined : handleClearBlockedLogsClick}
              customerId={isAllUserManagement ? null : (selectedCustomerId || currentUser?.customer?.id || null)}
              includeDeleted={blocked.includeDeleted}
              onIncludeDeletedChange={(include) => {
                setBlocked({ includeDeleted: include });
              }}
              customers={customers.list}
              isSuperAdmin={true}
              useUrlSync={true}
            />
          </TabsContent>
        )}

        {(isMyUserManagement || isAdmin) && (
          <TabsContent value={USER_MANAGEMENT_TABS.SETTINGS} className="space-y-4">
            <UserSettingsTab customer={currentUser?.customer} />
          </TabsContent>
        )}
      </Tabs>

      <EditUserDialog
        user={editingUser}
        loading={loading}
        onClose={() => {
          setEditingUser(null);
          setOriginalUser(null);
        }}
        onSave={handleUpdateUser}
      />

      <ChangeUserPasswordDialog
        user={passwordChangeUser}
        onClose={() => setPasswordChangeUser(null)}
        onSubmit={handlers.password}
      />

      <AuthTypeChangeDialog
        user={authTypeChangeUser}
        onClose={() => setAuthTypeChangeUser(null)}
        onSubmit={handlers.authType}
      />

      <ConfirmDialog
        open={confirmDialogs.deactivate.open}
        title="사용자 비활성화"
        description={`정말로 "${confirmDialogs.deactivate.target?.email}" 사용자를 비활성화하시겠습니까?`}
        confirmText="비활성화"
        cancelText="취소"
        variant="destructive"
        onConfirm={handlers.deactivate.confirm}
        onCancel={() => {
          setConfirmDialogs({ deactivate: { target: null, open: false } });
        }}
      />

      <InviteUserDialogWithCustomer
        open={showInviteForm}
        onClose={() => setShowInviteForm(false)}
        onSubmit={handleInviteUsers}
        loading={inviteLoading}
        customers={customers.list}
        showCustomerSelect={isSaasAdmin && activeTab === USER_MANAGEMENT_TABS.ALL}
      />

      <ConfirmDialog
        open={blocked.confirmOpen}
        title="차단목록 삭제"
        description="정말로 최근 차단목록을 모두 삭제하시겠습니까?"
        confirmText="삭제"
        cancelText="취소"
        variant="destructive"
        onConfirm={handleClearBlockedLogs}
        onCancel={() => setBlocked({ confirmOpen: false })}
      />

      <AlertDialog
        open={alertOpen}
        title={alertTitle}
        message={alertMessage}
        confirmText="확인"
        variant={alertTitle === '오류' ? 'destructive' : 'default'}
        onConfirm={() => setAlertOpen(false)}
      />
    </div>
  );
};

const UserManagement: React.FC = () => {
  return (
    <UserManagementProvider>
      <UserManagementInner />
    </UserManagementProvider>
  );
};

export default UserManagement;
